import {CheckboxTree, Search} from 'platform/components';
import {Box, Stack, VStack} from 'platform/foundation';

import {useState} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {GetAllEnumsResponse} from '../../types/api/api';
import {
  filterFeaturesBySearch,
  getFilteredFeaturesWithParents,
  getRowOptions,
  getTreeOptions,
} from './utils/getTreeOptions';
import {toggleFeatureSelection} from './utils/toggleFeatureSelection';

interface FeaturesCheckboxTreeProps extends TestIdProps {
  value: string[] | null;
  features: GetAllEnumsResponse[number]['keys'];
  isDisabled?: boolean;
  isMultiselect?: boolean;
  onChange: (value: string[]) => void;
}

export function FeaturesCheckboxTree(props: FeaturesCheckboxTreeProps) {
  const {isMultiselect = false} = props;
  const [searchValue, setSearchValue] = useState<string | null>(null);

  const handleSingleChange = (featureKey: string) => {
    props.onChange(toggleFeatureSelection(props.value, featureKey, props.features));
  };

  const handleMultipleChange = (value: string[] | null) => {
    props.onChange(value ?? []);
  };

  const checkboxTreeChangeProps = isMultiselect
    ? {onChange: handleMultipleChange}
    : {onSingleValueChange: handleSingleChange};

  const filteredFeatures = filterFeaturesBySearch(props.features, searchValue);
  const visibleFeatures = getFilteredFeaturesWithParents(filteredFeatures, props.features);
  const treeOptions = getTreeOptions(visibleFeatures);
  const [firstOptionsRow, secondOptionsRow] = getRowOptions(treeOptions);

  return (
    <VStack spacing={6} width="100%">
      <Search
        value={searchValue}
        onChange={setSearchValue}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('featureSearch', props)}
      />
      <Stack direction={['column', 'column', 'row', 'row']} spacing={[2, 2, 4, 4]}>
        <Box flex={1}>
          <CheckboxTree
            value={props.value}
            {...checkboxTreeChangeProps}
            options={firstOptionsRow}
            isDisabled={props.isDisabled}
            data-testid={suffixTestId('firstOptionsRow', props)}
          />
        </Box>
        <Box flex={1}>
          <CheckboxTree
            value={props.value}
            {...checkboxTreeChangeProps}
            options={secondOptionsRow}
            isDisabled={props.isDisabled}
            data-testid={suffixTestId('secondOptionsRow', props)}
          />
        </Box>
      </Stack>
    </VStack>
  );
}
